import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  fetchClubUsers,
  fetchCommunityUsers,
  fetchOrganizationUsers,
  fetchSuperAdminUserProfile,
  archiveSuperAdminUser,
  updateSuperAdminUserEnabledStatus,
} from '../../../services/superAdminService';
import { DEFAULT_ROLE_FILTER } from '../constants/entityUsers';
import EntityUsersSection from '../components/EntityUsersSection';
import UserProfileModal from '../components/UserProfileModal';
import AssignRoleModal from '../../../ui/components/admin/AssignRoleModal';
import PageShell from '../../../components/layout/PageShell';
import ConfirmationDialog from '../components/ConfirmationDialog';
import PageHeader from '../components/PageHeader';
import StatusChip from '../components/StatusChip';
import { formatAdminDate, safeAdminError, shortenIdentifier } from '../components/adminPresentation';

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const CONFIG = {
  organization: {
    singular: 'organization',
    title: 'Organization details',
    listPath: '/sa/organizations',
    fetchUsers: fetchOrganizationUsers,
    idParam: 'organizationId',
    entityKey: 'organization',
  },
  club: {
    singular: 'club',
    title: 'Club details',
    listPath: '/sa/clubs',
    fetchUsers: fetchClubUsers,
    idParam: 'clubId',
    entityKey: 'club',
  },
  community: {
    singular: 'community',
    title: 'Community details',
    listPath: '/sa/communities',
    fetchUsers: fetchCommunityUsers,
    idParam: 'communityId',
    entityKey: 'community',
  },
};

const SEARCH_DEBOUNCE_MS = 400;

const EntityDetailsPage = ({ entityType }) => {
  const config = CONFIG[entityType];
  const { token } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [entity, setEntity] = useState(location.state?.[config.singular] || null);
  const [users, setUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [status, setStatus] = useState('active');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    totalItems: 0,
    totalPages: 1,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyUserIds, setBusyUserIds] = useState({});
  const [actionStatus, setActionStatus] = useState(null);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [archiveCandidate, setArchiveCandidate] = useState(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [toastMessage, setToastMessage] = useState('');

  const entityId = params[config.idParam];

  useEffect(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSelectedRoles([]);
    setStatus('active');
    setPage(1);
    setPageSize(25);
    setUsers([]);
    setPagination({ page: 1, limit: 25, totalItems: 0, totalPages: 1 });
    setError('');
    setActionStatus(null);
    setBusyUserIds({});
  }, [entityId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      if (!token || !entityId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError('');
      setActionStatus(null);
      setBusyUserIds({});

      try {
        const response = await config.fetchUsers({
          token,
          [config.idParam]: entityId,
          roles: selectedRoles,
          q: debouncedSearchTerm,
          status,
          page,
          limit: pageSize,
          signal: controller.signal,
        });

        setEntity(response[config.entityKey] || null);
        setUsers(response.items || response.users || []);
        setAvailableRoles(response.availableRoles || []);
        setPagination({
          page: response.page || page,
          limit: response.limit || pageSize,
          totalItems: response.totalItems || 0,
          totalPages: response.totalPages || 1,
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }
        setError(safeAdminError(err, 'Unable to load entity details.'));
      } finally {
        setIsLoading(false);
      }
    };

    load();

    return () => controller.abort();
  }, [config, debouncedSearchTerm, entityId, page, pageSize, refreshTick, selectedRoles, status, token]);

  useEffect(() => {
    if (!toastMessage) return undefined;
    const timer = setTimeout(() => setToastMessage(''), 3000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  const handleRoleToggle = (role) => {
    setPage(1);

    if (role === DEFAULT_ROLE_FILTER) {
      setSelectedRoles([]);
      return;
    }

    setSelectedRoles((previous) => (
      previous.includes(role)
        ? previous.filter((item) => item !== role)
        : [...previous, role]
    ));
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleStatusChange = (nextStatus) => {
    setStatus(nextStatus);
    setPage(1);
  };

  const handlePageSizeChange = (nextPageSize) => {
    setPageSize(nextPageSize);
    setPage(1);
  };


  const markUserBusy = (userId, busy) => {
    setBusyUserIds((previous) => {
      const next = { ...previous };
      if (busy) {
        next[userId] = true;
      } else {
        delete next[userId];
      }
      return next;
    });
  };

  const handleViewProfile = async (user) => {
    if (!user?._id || !token) return;
    setIsProfileModalOpen(true);
    setIsProfileLoading(true);
    setProfileError('');

    try {
      const response = await fetchSuperAdminUserProfile({ token, userId: user._id });
      setSelectedUserProfile(response.user || null);
    } catch (err) {
      setProfileError(err.message || 'Unable to load user profile.');
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handleToggleEnabled = async (user, enabled) => {
    if (!user?._id || !token) return;
    const userId = user._id;
    const previous = users;
    setActionStatus(null);
    setUsers((current) => current.map((item) => (item._id === userId ? { ...item, enabled } : item)));
    markUserBusy(userId, true);

    try {
      const response = await updateSuperAdminUserEnabledStatus({ token, userId, enabled });
      const updated = response.user;
      setUsers((current) => current.map((item) => (item._id === userId ? { ...item, ...updated } : item)));
      setActionStatus({ tone: 'success', message: response.message || 'User status updated.' });
    } catch (err) {
      setUsers(previous);
      setActionStatus({ tone: 'danger', message: safeAdminError(err, 'Unable to update user status.') });
    } finally {
      markUserBusy(userId, false);
    }
  };

  const handleRequestArchive = (user) => {
    setArchiveCandidate(user || null);
  };

  const confirmArchive = async () => {
    if (!archiveCandidate?._id || !token) return;
    const userId = archiveCandidate._id;
    markUserBusy(userId, true);
    setActionStatus(null);

    try {
      const response = await archiveSuperAdminUser({ token, userId, archived: true });
      const updated = response.user;
      setUsers((current) => (
        status === 'active'
          ? current.filter((item) => item._id !== userId)
          : current.map((item) => (item._id === userId ? { ...item, ...updated } : item))
      ));
      setActionStatus({ tone: 'success', message: response.message || 'User archived successfully.' });
      setArchiveCandidate(null);
    } catch (err) {
      setActionStatus({ tone: 'danger', message: safeAdminError(err, 'Unable to archive user.') });
    } finally {
      markUserBusy(userId, false);
    }
  };

  return (
    <PageShell containerClassName="p-0">
    <div className="sa-workspace">
      <button type="button" className="sa-tertiary-button sa-back-button" onClick={() => navigate(config.listPath)}>← Back to {config.singular}s</button>
      <PageHeader eyebrow={config.title} title={entity?.name || 'Entity'} description={`Manage this ${config.singular}'s administrators, access, and operational context.`} actions={<button type="button" className="sa-primary-button" onClick={() => setIsAssignModalOpen(true)}>{`Assign ${config.singular} administrator`}</button>} />

      {actionStatus && <div className={`sa-inline-feedback sa-inline-feedback--${actionStatus.tone === 'success' ? 'success' : 'danger'}`} role="status">{actionStatus.message}</div>}
      {error ? <div className="sa-error-state" role="alert"><strong>Entity details unavailable</strong><span>{error}</span></div> : <div className="sa-detail-layout"><section className="sa-panel"><div className="sa-panel__body"><EntityUsersSection
            users={users}
            availableRoles={availableRoles}
            selectedRoles={selectedRoles}
            onRoleToggle={handleRoleToggle}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            status={status}
            onStatusChange={handleStatusChange}
            isLoading={isLoading}
            page={pagination.page}
            limit={pagination.limit}
            totalItems={pagination.totalItems}
            totalPages={pagination.totalPages}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            busyUserIds={busyUserIds}
            onViewProfile={handleViewProfile}
            onToggleEnabled={handleToggleEnabled}
            onArchive={handleRequestArchive}
          /></div></section><aside className="sa-detail-sidebar"><section className="sa-panel"><header className="sa-panel__header"><div><h2>Context</h2><p>Canonical record summary.</p></div></header><dl className="sa-detail-list sa-detail-list--borderless"><div><dt>Status</dt><dd><StatusChip value={entity?.isActive === false ? 'Archived' : 'Active'} /></dd></div><div><dt>Type</dt><dd>{config.singular}</dd></div><div><dt>Created</dt><dd>{formatAdminDate(entity?.createdAt)}</dd></div><div><dt>Identifier</dt><dd>{shortenIdentifier(entityId)}</dd></div></dl></section></aside></div>}

      <AssignRoleModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        token={token}
        entityType={config.singular}
        entityId={entityId}
        onAssigned={(response) => {
          const successMessage = response?.message || 'Admin assigned successfully.';
          setActionStatus({ tone: 'success', message: successMessage });
          setToastMessage(successMessage);
          setSelectedRoles([]);
          setStatus('active');
          setPage(1);
          setRefreshTick((value) => value + 1);
          window.dispatchEvent(new CustomEvent('entity-admin-role-assigned', {
            detail: { entityType: config.singular, entityId },
          }));
        }}
      />

      {toastMessage && (
        <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1200 }}>
          <div className="toast show align-items-center text-bg-success border-0" role="status" aria-live="polite">
            <div className="d-flex">
              <div className="toast-body">{toastMessage}</div>
              <button
                type="button"
                className="btn-close btn-close-white me-2 m-auto"
                aria-label="Close"
                onClick={() => setToastMessage('')}
              />
            </div>
          </div>
        </div>
      )}

      <UserProfileModal
        user={selectedUserProfile}
        isOpen={isProfileModalOpen}
        isLoading={isProfileLoading}
        error={profileError}
        onClose={() => {
          setIsProfileModalOpen(false);
          setSelectedUserProfile(null);
          setProfileError('');
        }}
      />

      <ConfirmationDialog open={Boolean(archiveCandidate)} title="Archive this user?" entityName={archiveCandidate?.name || archiveCandidate?.email} consequence={`This user will be removed from the active ${config.singular} member list.`} unaffected="Their CrickArena account and historical cricket records are not deleted." confirmLabel="Archive user" busy={Boolean(archiveCandidate && busyUserIds[archiveCandidate._id])} onCancel={() => setArchiveCandidate(null)} onConfirm={confirmArchive} />

    </div>
  
    </PageShell>
  );
};

export default EntityDetailsPage;
